import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr, fileUrl } from "@/lib/api";

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [loading, setLoading] = useState(false);
  const [c, setC] = useState({});
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const next = params.get("next") || "/app";

  useEffect(() => { api.get("/content").then((r) => setC(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, navigate, next]);
  const t = (k, fb = "") => (c[k] != null && c[k] !== "" ? c[k] : fb);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bienvenue sur Lisungi AI");
      } else {
        await register(form);
        toast.success("Compte créé — bienvenue !");
      }
      navigate(next, { replace: true });
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-lisungi-bg">
      {/* Left visual */}
      <div className="hidden lg:flex relative bg-navy text-white flex-col justify-between p-12 overflow-hidden">
        {c.auth_image && <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${fileUrl(c.auth_image)})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
        <div className="relative"><Logo variant="light" size="lg" /></div>
        <div className="relative">
          <h2 className="font-serif text-4xl font-bold leading-tight">{t("auth_side_title", "Votre espace de recherche scientifique.")}</h2>
          <p className="text-white/70 mt-5 max-w-md leading-relaxed">{t("auth_side_text")}</p>
        </div>
        <p className="relative text-xs text-white/40 font-mono">Assistant intelligent conçu par le LARSICOM.</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo variant="dark" /></div>
          <h1 className="font-serif text-3xl font-bold text-navy">{mode === "login" ? t("auth_login_title", "Connexion") : t("auth_register_title", "Créer un compte")}</h1>
          <p className="text-muted2 mt-2 text-sm">{mode === "login" ? t("auth_login_sub", "Reprenez votre recherche là où vous l'avez laissée.") : t("auth_register_sub", "Rejoignez la plateforme scientifique du LARSICOM.")}</p>

          <Button onClick={googleLogin} variant="outline" className="w-full mt-6 h-11 rounded-lg border-[#E5E7EB] bg-white hover:bg-lisungi-bg gap-3" data-testid="google-login-btn">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
            Continuer avec Google
          </Button>
          <div className="flex items-center gap-3 my-5 text-xs text-muted2">
            <div className="h-px bg-[#E5E7EB] flex-1" /> OU <div className="h-px bg-[#E5E7EB] flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-11" data-testid="register-name" placeholder="Votre nom" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 h-11" data-testid="auth-email" placeholder="vous@exemple.com" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5 h-11" data-testid="auth-password" placeholder="••••••••" />
            </div>
            {mode === "register" && (
              <div>
                <Label>Profil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="register-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Étudiant</SelectItem>
                    <SelectItem value="researcher">Chercheur</SelectItem>
                    <SelectItem value="supervisor">Encadreur</SelectItem>
                    <SelectItem value="user">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg bg-navy text-white hover:bg-navy-800 font-semibold" data-testid="auth-submit">
              {loading ? "Veuillez patienter…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <p className="text-sm text-muted2 mt-6 text-center">
            {mode === "login" ? "Pas encore de compte ? " : "Déjà inscrit ? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-navy font-semibold hover:underline" data-testid="auth-toggle">
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
          <Link to="/" className="block text-center text-xs text-muted2 mt-4 hover:text-navy">← Retour à l'accueil</Link>
        </motion.div>
      </div>
    </div>
  );
}
